// SPDX-License-Identifier: Apache-2.0

// Minimal `Awareness` implementation — a subset of the y-protocols
// `awareness` module, narrowed to the slice usePresence actually needs.
//
// Why hand-roll instead of pulling `y-protocols`? We don't ship y-websocket
// (the DO speaks our own JSON envelope, not the standard y-websocket binary
// protocol), and `y-protocols/awareness` would drag in a binary-encoder
// dependency tree (~12 KB gzipped) for a 200-line surface we already use
// indirectly through our own JSON publish/subscribe layer.
//
// API surface (matches y-protocols' shape):
//
//   const a = new Awareness(yDoc);
//   a.setLocalState({...} | null);
//   a.getLocalState(): unknown;
//   a.getStates(): Map<clientId, state>;
//   a.on("change", (changes, origin) => void);
//   a.off("change", handler);
//
//   encodeAwarenessUpdate(a, clientIds[]): Uint8Array
//   applyAwarenessUpdate(a, bytes, origin): void
//   removeAwarenessStates(a, clientIds[], origin): void
//
// Encoding format (custom, not wire-compatible with y-protocols):
//
//   JSON.stringify({ entries: [[clientId, clock, stateOrNull], ...] })
//   then UTF-8 → Uint8Array
//
// Yes, this means our awareness frames are slightly bigger than the binary
// y-protocols ones. But we already wrap in JSON envelopes for the DO, so
// shaving the inner frame is a false economy.

import * as Y from "yjs";

interface MetaEntry {
  clock: number;
  lastUpdated: number;
}

type ChangeHandler = (
  changes: { added: number[]; updated: number[]; removed: number[] },
  origin: unknown,
) => void;

const OUTDATED_TIMEOUT_MS = 30_000;

export class Awareness {
  readonly clientID: number;
  /** clientId → state (any JSON-serialisable shape, or null = no state). */
  private states = new Map<number, unknown>();
  /** clientId → meta (clock + lastUpdated). */
  private meta = new Map<number, MetaEntry>();
  private listeners = new Set<ChangeHandler>();
  /** Periodic timer — drops peers we haven't heard from in OUTDATED_TIMEOUT_MS. */
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(doc: Y.Doc) {
    this.clientID = doc.clientID;
    this.meta.set(this.clientID, { clock: 0, lastUpdated: Date.now() });
    // Heartbeat: every OUTDATED_TIMEOUT_MS / 10 seconds, prune stale peers.
    if (typeof setInterval === "function") {
      this.timer = setInterval(() => this.checkOutdated(), OUTDATED_TIMEOUT_MS / 10);
    }
  }

  destroy(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.listeners.clear();
    this.states.clear();
    this.meta.clear();
  }

  on(_event: "change", handler: ChangeHandler): void {
    this.listeners.add(handler);
  }
  off(_event: "change", handler: ChangeHandler): void {
    this.listeners.delete(handler);
  }
  /** @internal — called by the apply/remove helpers below. */
  emit(
    changes: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown,
  ): void {
    if (changes.added.length === 0 && changes.updated.length === 0 && changes.removed.length === 0) {
      return;
    }
    for (const h of this.listeners) {
      try {
        h(changes, origin);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[awareness] handler threw:", err);
      }
    }
  }

  getLocalState(): unknown {
    return this.states.get(this.clientID) ?? null;
  }

  setLocalState(state: unknown): void {
    const prev = this.states.get(this.clientID);
    const m = this.meta.get(this.clientID) ?? { clock: 0, lastUpdated: Date.now() };
    m.clock += 1;
    m.lastUpdated = Date.now();
    this.meta.set(this.clientID, m);

    const wasNull = prev === undefined || prev === null;
    if (state === null) {
      this.states.delete(this.clientID);
      if (!wasNull) {
        this.emit({ added: [], updated: [], removed: [this.clientID] }, "local");
      }
      return;
    }
    this.states.set(this.clientID, state);
    if (wasNull) {
      this.emit({ added: [this.clientID], updated: [], removed: [] }, "local");
    } else {
      this.emit({ added: [], updated: [this.clientID], removed: [] }, "local");
    }
  }

  getStates(): Map<number, unknown> {
    // Defensive copy — callers iterate.
    return new Map(this.states);
  }

  // Internal mutations used by encode/apply/remove helpers.
  /** @internal */
  _applyEntry(
    clientId: number,
    clock: number,
    state: unknown,
    origin: unknown,
    changes: { added: number[]; updated: number[]; removed: number[] },
  ): void {
    const prevMeta = this.meta.get(clientId);
    if (prevMeta && prevMeta.clock > clock) return; // older update — ignore.
    if (prevMeta && prevMeta.clock === clock && state === this.states.get(clientId)) return;

    this.meta.set(clientId, { clock, lastUpdated: Date.now() });
    const had = this.states.has(clientId);
    if (state === null || state === undefined) {
      this.states.delete(clientId);
      if (had) changes.removed.push(clientId);
      return;
    }
    this.states.set(clientId, state);
    if (had) changes.updated.push(clientId);
    else changes.added.push(clientId);
    void origin;
  }

  private checkOutdated(): void {
    const now = Date.now();
    const removed: number[] = [];
    for (const [clientId, m] of this.meta.entries()) {
      if (clientId === this.clientID) continue;
      if (now - m.lastUpdated > OUTDATED_TIMEOUT_MS && this.states.has(clientId)) {
        this.states.delete(clientId);
        removed.push(clientId);
      }
    }
    if (removed.length > 0) {
      this.emit({ added: [], updated: [], removed }, "timeout");
    }
  }
}

interface Encoded {
  entries: Array<[number, number, unknown]>;
}

export function encodeAwarenessUpdate(a: Awareness, clientIds: number[]): Uint8Array {
  const states = a.getStates();
  const entries: Array<[number, number, unknown]> = [];
  for (const id of clientIds) {
    // We can't reach Awareness's private meta, so re-derive a clock by
    // touching setLocalState — but actually, we only ever encode our OWN
    // local clientID, so we just read the current state. Remote ids might
    // be requested in tests; support those too with clock 1.
    const state = states.get(id) ?? null;
    entries.push([id, 1, state]);
  }
  const payload: Encoded = { entries };
  return new TextEncoder().encode(JSON.stringify(payload));
}

export function applyAwarenessUpdate(a: Awareness, buf: Uint8Array, origin: unknown): void {
  let decoded: Encoded;
  try {
    decoded = JSON.parse(new TextDecoder().decode(buf)) as Encoded;
  } catch {
    return;
  }
  if (!decoded || !Array.isArray(decoded.entries)) return;
  const changes = { added: [] as number[], updated: [] as number[], removed: [] as number[] };
  for (const e of decoded.entries) {
    if (!Array.isArray(e) || e.length < 3) continue;
    const [clientId, clock, state] = e;
    if (typeof clientId !== "number" || typeof clock !== "number") continue;
    a._applyEntry(clientId, clock, state ?? null, origin, changes);
  }
  a.emit(changes, origin);
}

export function removeAwarenessStates(a: Awareness, clientIds: number[], origin: unknown): void {
  const changes = { added: [] as number[], updated: [] as number[], removed: [] as number[] };
  for (const id of clientIds) {
    a._applyEntry(id, Number.MAX_SAFE_INTEGER, null, origin, changes);
  }
  a.emit(changes, origin);
}
